import type {
  ExtractedCategoryOptions,
  PnlCategory,
  DriverEntryStructure,
  CombinedPostResult,
  ExtractedUnalocated,
} from "../../models/UtilFunctions.model";

/** Numeric/text cleaner for inputs and currency-like strings. */
export function cleanValue(raw: string, isDEFormat: boolean): number | string | null {
  if (!raw || raw === "-") return null;
  if (["mt", "days", "USD"].includes(raw)) return raw;

  let cleaned = raw.replace(/\s*\$\s*/g, "");
  if (isDEFormat) cleaned = cleaned.replace(/,/g, "");
  if (/^\(.*\)$/.test(cleaned)) cleaned = "-" + cleaned.slice(1, -1);

  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

/** Filter/normalize POST payload fields; drop zero-impact entries. */
export function normalizePostData(input: Record<string, string>): Record<string, number | string> {
  const result: Record<string, number | string> = {};

  for (const [key, value] of Object.entries(input)) {
    if (key.endsWith("Comments")) continue;

    const isQuantity = key.includes("Quantity");
    const isOption = key.includes("Option");
    const isRate = key.includes("Rate");
    const isImpact = key.includes("Impact");

    const num = cleanValue(value, isImpact);
    if (num === null) continue;

    if (isImpact) {
      const quantityNum = cleanValue(input[key.replace("Impact", "Quantity")], false);
      if (quantityNum === null || num === 0) continue;
    } else if (isQuantity) {
      const impactNum = cleanValue(input[key.replace("Quantity", "Impact")], true);
      if (impactNum === null || impactNum === 0) continue;
    } else if (isRate) {
      const impactNum = cleanValue(input[key.replace("Rate", "Impact")], true);
      if (impactNum === null || impactNum === 0) continue;
    } else if (isOption) {
      const impactNum = cleanValue(input[key.replace("Option", "Impact")], true);
      if (impactNum === null || impactNum === 0) continue;
    }
    result[key] = num;
  }
  return result;
}

/** Canonicalize tokens: lowercase, non-alphanum => "_", trim underscores. */
export const canonicalize = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/** Extract category and driver from a key like "<category>-<Field>-<driver_snake>". */
export function extractRowSubNames(key: string) {
  if (!key) return { categoryName: "", driverName: "" };
  const parts = key.split("-");
  const categoryName = parts[0]?.replace(/_/g, " ") || "";
  const driverName = parts.length > 2 ? parts.slice(2).join("-").replace(/_/g, " ") : "";
  return { categoryName, driverName };
}

/** Group all "X-Impact[-...]" values per category, respecting a whitelist. */
export function extractImpactPerCategory(
  data: Record<string, string>,
  categoriesWhitelist: string[],
): Record<string, number> {
  const res: Record<string, number> = {};
  for (const [key, value] of Object.entries(data)) {
    const m = key.match(/^(.+)-Impact(?:-|$)/);
    if (!m) continue;

    const category = m[1].replace(/_/g, " ");
    if (!categoriesWhitelist.includes(category)) continue;

    const impact = cleanValue(value, true) ?? 0;
    if (typeof impact === "number") {
      res[category] = (res[category] ?? 0) + impact;
    }
  }
  return res;
}

/** Build driver row keys for a driver across all non-excluded PnL categories. */
export const NamingDriverRows = (text: string, pnlTableRows: PnlCategory[], excludedCategories: string[]) =>
  pnlTableRows
    .filter((row) => !excludedCategories.includes(row.category))
    .map((row) => {
      const name = row.category.split(" ").join("_");
      const suffix = text.split(" ").join("_");
      return [
        `${name}-Quantity-${suffix}`,
        `${name}-Option-${suffix}`,
        `${name}-Rate-${suffix}`,
        `${name}-Impact-${suffix}`,
      ];
    });

/** Extract category+options for downstream usage. */
export function extractCategoriesAndTheirOptions(data: PnlCategory[]): ExtractedCategoryOptions[] {
  return data.map(({ category, options }) => ({ category, options }));
}

/** Assemble the POST payload for voyage driver data. */
export function combinedPostDriverData(
  tcCode: string,
  voyageNumber: number,
  imoNumber: string,
  vesselName: string,
  submittedBy: string,
  extractedUnallocated: ExtractedUnalocated[],
  driversComments: Record<string, string>,
  driversQuantitiesAndImpacts: Record<string, number | string>,
): CombinedPostResult {
  const driverMap: Record<string, DriverEntryStructure> = {};

  // Comments
  for (const [key, comment] of Object.entries(driversComments)) {
    const driverKey = key.replace("-Comments", "");
    const driverName = driverKey.replace(/_/g, " ");
    driverMap[driverKey] = {
      driver: driverName,
      comments: comment,
      driverCategoryImpact: [],
    };
  }

  // Values
  for (const [key, value] of Object.entries(driversQuantitiesAndImpacts)) {
    const [categoryRaw, type, driverKey] = key.split("-");
    const category = categoryRaw.replace(/_/g, " ");
    const entry = driverMap[driverKey];
    if (!entry) continue;

    let impactEntry = entry.driverCategoryImpact.find((impact) => impact.category === category);
    if (!impactEntry) {
      impactEntry = { category, qty: 0, impact: 0, rate: null, unit: "" };
      entry.driverCategoryImpact.push(impactEntry);
    }

    if (type === "Quantity" && typeof value === "number") {
      impactEntry.qty = value;
    } else if (type === "Impact" && typeof value === "number") {
      impactEntry.impact = value;
    } else if (type === "Rate" && typeof value === "number") {
      impactEntry.rate = value;
    } else if (type === "Option") {
      impactEntry.unit = value as string;
    }
  }

  // Filter empty entries
  const filteredDrivers = Object.values(driverMap).filter(
    (entry) => entry.comments.trim() !== "" || entry.driverCategoryImpact.length > 0,
  );

  return {
    tcCode,
    voyageNumber,
    imoNumber,
    vesselName,
    status: "Submitted",
    submittedBy,
    unallocated: extractedUnallocated,
    addDrivers: filteredDrivers,
  };
}
